/**
 * Lightweight performance timers for diagnosing slow page loads.
 *
 * Enabled when:
 *   - NODE_ENV === 'development', OR
 *   - localStorage.setItem('rc-perf', '1') then reload (works in production)
 *
 * Disable in production: localStorage.removeItem('rc-perf')
 */

const PREFIX = '[RC Perf]';

let helpLogged = false;
const callCounts = new Map<string, number>();

function logHelpOnce(): void {
  if (helpLogged || !isPerfEnabled()) return;
  helpLogged = true;
  // eslint-disable-next-line no-console
  console.info(
    `${PREFIX} Timers active. In production: localStorage.setItem('rc-perf','1') then reload. Disable: localStorage.removeItem('rc-perf')`
  );
}

export function isPerfEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV === 'development') return true;
  try {
    return localStorage.getItem('rc-perf') === '1';
  } catch {
    return false;
  }
}

/** Log a point-in-time mark (optional detail string). */
export function perfMark(label: string, detail?: string): void {
  if (!isPerfEnabled()) return;
  logHelpOnce();
  const extra = detail ? ` — ${detail}` : '';
  // eslint-disable-next-line no-console
  console.log(`${PREFIX} ${label}${extra}`);
}

/** Count repeated calls (e.g. duplicate GET /progress/SAA). */
export function perfCount(label: string): number {
  if (!isPerfEnabled()) return 0;
  logHelpOnce();
  const n = (callCounts.get(label) ?? 0) + 1;
  callCounts.set(label, n);
  // eslint-disable-next-line no-console
  console.log(`${PREFIX} ${label} (#${n})`);
  return n;
}

export function perfResetCounts(): void {
  callCounts.clear();
}

/** Print sorted call-count summary to the console. */
export function perfSummary(title: string): void {
  if (!isPerfEnabled() || callCounts.size === 0) return;
  const rows = Array.from(callCounts.entries()).sort((a, b) => b[1] - a[1]);
  // eslint-disable-next-line no-console
  console.group(`${PREFIX} Summary — ${title}`);
  rows.forEach(([label, count]) => {
    // eslint-disable-next-line no-console
    console.log(`  ${count}×  ${label}`);
  });
  // eslint-disable-next-line no-console
  console.groupEnd();
}

export async function perfTimeAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
  if (!isPerfEnabled()) return fn();
  logHelpOnce();
  const start = performance.now();
  try {
    return await fn();
  } finally {
    const ms = performance.now() - start;
    // eslint-disable-next-line no-console
    console.log(`${PREFIX} ${label}: ${ms.toFixed(0)}ms`);
  }
}

export function perfTimeSync<T>(label: string, fn: () => T): T {
  if (!isPerfEnabled()) return fn();
  logHelpOnce();
  const start = performance.now();
  try {
    return fn();
  } finally {
    const ms = performance.now() - start;
    // eslint-disable-next-line no-console
    console.log(`${PREFIX} ${label}: ${ms.toFixed(0)}ms`);
  }
}

export function perfGroupStart(label: string): void {
  if (!isPerfEnabled()) return;
  logHelpOnce();
  // eslint-disable-next-line no-console
  console.group(`${PREFIX} ${label}`);
}

export function perfGroupEnd(): void {
  if (!isPerfEnabled()) return;
  // eslint-disable-next-line no-console
  console.groupEnd();
}
